import { useState, useEffect } from 'react';
import { questionnaireService } from '../services/questionnaireService';
import { Questionnaire, QuestionnaireValidation } from '../types/questionnaire';

export const useQuestionnaire = (serviceId?: number, serviceName?: string) => {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuestionnaireById = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await questionnaireService.getForServiceId(id);
      setQuestionnaire(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setQuestionnaire(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionnaireByName = async (name: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await questionnaireService.getForService(name);
      setQuestionnaire(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setQuestionnaire(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Priorité à l'ID si disponible
    if (serviceId) {
      fetchQuestionnaireById(serviceId);
    } else if (serviceName) {
      fetchQuestionnaireByName(serviceName);
    }
  }, [serviceId, serviceName]);

  return { questionnaire, loading, error, refetch: fetchQuestionnaireById };
};

export const useQuestionnaireValidation = () => {
  const [validation, setValidation] = useState<QuestionnaireValidation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateBooking = async (bookingId: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await questionnaireService.validateBooking(bookingId);
      setValidation(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setValidation(null);
    } finally {
      setLoading(false);
    }
  };

  const submitQuestionnaire = async (bookingId: number, questionnaireType: string, responses: Record<string, boolean | string>) => {
    setLoading(true);
    setError(null);
    try {
      const data = await questionnaireService.submit({
        booking_id: bookingId,
        questionnaire_type: questionnaireType,
        responses
      });
      setValidation(data);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { validation, loading, error, validateBooking, submitQuestionnaire };
};
